// Global Instructions Rule Applied!
// Shape of GET /fund-transactions/fund/:fundId/transfer-candidates
// (view__fund_transfer_candidates): the commitment picker of a Transfer of
// Interest fund transaction - one row per live standalone commitment of the
// fund with its position, the terms the transferee inherits by default, and
// its selection state for the transaction being edited.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const FundTransferCandidateRowSchema = z
  .object({
    fund_commitments__id: z.string(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    // the investor + amount / closing date when it has two commitments here
    prog__commitment_label: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__amount_fmt: z.string().nullish(),
    fund_commitment_statuses__id: z.number().nullish(),
    fund_commitment_statuses__label: z.string().nullish(),
    // YYYY-MM-DD text (the columns are timestamptz written as ::date)
    fund_commitments__date_committed: z.string().nullish(),
    fund_commitments__date_closed: z.string().nullish(),
    // the transferor's terms - prefilled into the transferee's
    fund_commitments__management_fee_rate: z.number().nullish(),
    fund_commitments__base_carry_rate: z.number().nullish(),
    fund_commitments__base_hurdle_multiple: z.number().nullish(),
    fund_commitments__stepped_carry_rate: z.number().nullish(),
    fund_commitments__stepped_hurdle_multiple: z.number().nullish(),
    // today's position (issued calls / contributions)
    prog__called: z.number().nullish(),
    prog__called_fmt: z.string().nullish(),
    prog__paid: z.number().nullish(),
    prog__paid_fmt: z.string().nullish(),
    prog__outstanding: z.number().nullish(),
    prog__outstanding_fmt: z.string().nullish(),
    // linked to the transaction being read (live link)
    prog__selected: z.boolean(),
    // on a live link of another Draft / Booked transfer: unselectable
    prog__transferred_elsewhere: z.boolean(),
    prog__transferred_by_label: z.string().nullish(),
    // Transferred / Settled status: unselectable
    prog__not_live: z.boolean()
  })
  .strict()

export const FundTransferCandidatesSchema = z
  .object({
    fund: z
      .object({
        funds__id: z.string().nullish(),
        funds__name: z.string().nullish(),
        currencies__symbol: z.string().nullish()
      })
      .passthrough(),
    rows: z.array(FundTransferCandidateRowSchema)
  })
  .strict()

export type FundTransferCandidateRow = z.infer<typeof FundTransferCandidateRowSchema>
export type FundTransferCandidates = z.infer<typeof FundTransferCandidatesSchema>

export const validateFundTransferCandidates = (data: unknown): FundTransferCandidates =>
  validateWithZodSchema(FundTransferCandidatesSchema, data, 'fund transfer candidates')
